// Street address for a position (Google Geocoding through our API, decision 9C). Each rounded
// position (~10 m) is asked once per page load; without a Google key the server answers 404 and
// callers show the coordinates instead. Copied in web/ and crm/. A position inside a geofence
// shows the geofence's name above the address (user decision, 25 Oct 2026).
import { CSSProperties, useEffect, useState } from 'react';
import { geofencesAt, useGeofences } from './geofenceStore';

const asked = new Map<string, Promise<string | null>>();

function lookup(lat: number, lon: number): Promise<string | null> {
  const key = `${lat.toFixed(4)},${lon.toFixed(4)}`;
  let p = asked.get(key);
  if (!p) {
    p = fetch(`/api/geocode?lat=${lat.toFixed(5)}&lon=${lon.toFixed(5)}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { address: null }))
      .then((d: { address?: string | null }) => d.address ?? null)
      .catch(() => null);
    p.then((a) => { if (a === null) setTimeout(() => asked.delete(key), 60_000); });   // try again later
    asked.set(key, p);
  }
  return p;
}

export function useAddress(lat: number | null | undefined, lon: number | null | undefined): string | null {
  const [address, setAddress] = useState<string | null>(null);
  const key = lat == null || lon == null ? '' : `${lat.toFixed(4)},${lon.toFixed(4)}`;
  useEffect(() => {
    if (!key) { setAddress(null); return; }
    let live = true;
    void lookup(lat!, lon!).then((a) => { if (live) setAddress(a); });
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return address;
}

// The geofences a position is in (smallest first), named above the street address.
function GeofenceName({ lat, lon }: { lat: number; lon: number }) {
  const found = geofencesAt(useGeofences(), lat, lon);
  if (!found.length) return null;
  return (
    <span className="geo-name">
      <span className="geo-dot" aria-hidden="true" style={{ '--geo': `var(--geofence-${found[0].colour})` } as CSSProperties} />
      {found.map((g) => g.name).join(', ')}
    </span>
  );
}

// The address, or the coordinates while it loads or when there is none; inside a geofence,
// its name first with the address under it.
export function Address({ lat, lon, coords = true }: { lat: number; lon: number; coords?: boolean }) {
  const address = useAddress(lat, lon);
  const text = address ? <>{address}</> : coords ? <span className="mono">{lat.toFixed(5)}, {lon.toFixed(5)}</span> : null;
  return <><GeofenceName lat={lat} lon={lon} />{text && <span className="geo-addr">{text}</span>}</>;
}

// Address with the coordinates small underneath (or just the coordinates).
export function Location({ lat, lon }: { lat: number; lon: number }) {
  const address = useAddress(lat, lon);
  const coords = <span className="mono">{lat.toFixed(5)}, {lon.toFixed(5)}</span>;
  return <><GeofenceName lat={lat} lon={lon} />{address ? <><span className="geo-addr">{address}</span><span className="loc-coords">{coords}</span></> : coords}</>;
}
